import { useCallback, useRef, useState } from 'react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RepeatIcon from '@mui/icons-material/Repeat';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RuleIcon from '@mui/icons-material/Rule';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { FxVendor, FxCategory, FxImportReviewRow, FxBankAccount } from '@/types/api/finance';
import { ImportStep, ColumnMapping } from './FinanceXImport.container';

type Props = {
  step: ImportStep;
  headers: string[];
  parsedRows: string[][];
  columnMapping: ColumnMapping;
  reviewRows: FxImportReviewRow[];
  vendors: FxVendor[];
  categories: FxCategory[];
  fileName: string;
  sourceAccount: string;
  bankAccounts: FxBankAccount[];
  onSourceAccountChange: (value: string) => void;
  invertSign: boolean;
  onInvertSignChange: (value: boolean) => void;
  isSaving: boolean;
  isLearning: boolean;
  error: string | null;
  autoMapped: boolean;
  onFileUpload: (file: File) => void;
  onColumnMappingChange: (mapping: ColumnMapping) => void;
  onStartProcessing: () => void;
  onUpdateRow: (tempId: string, updates: Partial<FxImportReviewRow>) => void;
  onAcceptAll: () => void;
  onConfirmImport: () => void;
  onReset: () => void;
  onGoToMapping: () => void;
};

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const confidenceColor = (c: number | null): string => {
  if (c === null) return '#9ca3af';
  if (c >= 0.8) return '#10B981';
  if (c >= 0.5) return '#F59E0B';
  return '#EF4444';
};

const ReviewRowInline = ({
  row,
  vendors,
  categories,
  onUpdateRow,
}: {
  row: FxImportReviewRow;
  vendors: FxVendor[];
  categories: FxCategory[];
  onUpdateRow: (tempId: string, updates: Partial<FxImportReviewRow>) => void;
}): JSX.Element => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      borderBottom: '1px solid #f3f4f6',
      '&:last-child': { borderBottom: 'none' },
      '&:hover': { backgroundColor: '#f9fafb' },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
        {row.description}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#6f7175' }}>
          {row.date}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: row.type === 'income' ? '#10B981' : '#202123' }}
        >
          {row.type === 'income' ? '+' : '-'}{formatCurrency(row.amount)}
        </Typography>
      </Box>
      {row.subscriptionName && (
        <Chip
          icon={<RepeatIcon />}
          label={row.subscriptionName}
          size="small"
          sx={{ mt: 0.5, fontSize: 10, height: 20, backgroundColor: '#8B5CF620', color: '#8B5CF6' }}
        />
      )}
      {row.aiReasoning && (
        <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
          AI: {row.aiReasoning}
        </Typography>
      )}
    </Box>

    <TextField
      select
      size="small"
      value={row.vendorId ?? ''}
      onChange={(e) => onUpdateRow(row.tempId, {
        vendorId: e.target.value || null,
        accepted: true,
        matchSource: 'manual',
      })}
      sx={{ minWidth: 160, '& .MuiInputBase-input': { fontSize: 12 } }}
      variant="outlined"
      label="Vendor"
    >
      <MenuItem value="" sx={{ fontSize: 12 }}>— None —</MenuItem>
      {row.newVendorName && (
        <MenuItem value="__new__" sx={{ fontSize: 12, fontStyle: 'italic', color: '#8B5CF6' }}>
          + {row.newVendorName} (new)
        </MenuItem>
      )}
      {vendors.map((v) => (
        <MenuItem key={v.id} value={v.id} sx={{ fontSize: 12 }}>
          {v.displayName || v.name}
        </MenuItem>
      ))}
    </TextField>

    <TextField
      select
      size="small"
      value={row.categoryId ?? ''}
      onChange={(e) => onUpdateRow(row.tempId, {
        categoryId: e.target.value || null,
        accepted: true,
      })}
      sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: 12 } }}
      variant="outlined"
      label="Category"
    >
      <MenuItem value="" sx={{ fontSize: 12 }}>— None —</MenuItem>
      {categories.map((c) => (
        <MenuItem key={c.id} value={c.id} sx={{ fontSize: 12 }}>
          {c.name}
        </MenuItem>
      ))}
    </TextField>

    {!row.accepted ? (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {(row.vendorId || row.newVendorName) && (
          <Button
            size="small"
            variant="contained"
            onClick={() => onUpdateRow(row.tempId, { accepted: true })}
            sx={{
              backgroundColor: '#10B981',
              '&:hover': { backgroundColor: '#059669' },
              textTransform: 'none',
              fontSize: 12,
              minWidth: 'auto',
              px: 2,
            }}
          >
            Accept
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={() => onUpdateRow(row.tempId, { accepted: false })}
          sx={{ textTransform: 'none', fontSize: 12, minWidth: 'auto', px: 2, color: '#6f7175', borderColor: '#d1d5db' }}
        >
          Skip
        </Button>
      </Box>
    ) : (
      <Chip
        label="Accepted"
        size="small"
        sx={{ fontSize: 10, height: 24, backgroundColor: '#10B98120', color: '#10B981' }}
      />
    )}
  </Box>
);

const FinanceXImportComponent = function ({
  step,
  headers,
  parsedRows,
  columnMapping,
  reviewRows,
  vendors,
  categories,
  fileName,
  sourceAccount,
  bankAccounts,
  onSourceAccountChange,
  invertSign,
  onInvertSignChange,
  isSaving,
  isLearning,
  error,
  autoMapped,
  onFileUpload,
  onColumnMappingChange,
  onStartProcessing,
  onUpdateRow,
  onAcceptAll,
  onConfirmImport,
  onReset,
  onGoToMapping,
}: Props): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showAutoAccepted, setShowAutoAccepted] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file && file.name.endsWith('.csv')) onFileUpload(file);
    },
    [onFileUpload]
  );

  // Computed stats
  const nonDuplicateRows = reviewRows.filter((r) => !r.isDuplicate);
  const duplicateCount = reviewRows.filter((r) => r.isDuplicate).length;
  const acceptedCount = nonDuplicateRows.filter((r) => r.accepted).length;
  const ruleCount = nonDuplicateRows.filter((r) => r.matchSource === 'rule').length;
  const aiCount = nonDuplicateRows.filter((r) => r.matchSource === 'ai').length;
  const unmatchedCount = nonDuplicateRows.filter((r) => !r.matchSource).length;

  const needsAttentionRows = nonDuplicateRows.filter((r) => !r.accepted);
  const autoAcceptedRows = nonDuplicateRows.filter((r) => r.accepted);

  const subscriptionCount = nonDuplicateRows.filter((r) => r.subscriptionId).length;

  const totalExpense = nonDuplicateRows
    .filter((r) => r.type === 'expense' && !r.isDuplicate)
    .reduce((sum, r) => sum + r.amount, 0);
  const totalIncome = nonDuplicateRows
    .filter((r) => r.type === 'income' && !r.isDuplicate)
    .reduce((sum, r) => sum + r.amount, 0);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Import Transactions
          </Typography>
          <Typography variant="body2" sx={{ color: '#6f7175', mt: 0.5 }}>
            Upload a bank CSV to auto-categorize transactions with rules and AI.
          </Typography>
        </Box>
        {step !== 'upload' && step !== 'done' && (
          <Button
            startIcon={<RestartAltIcon />}
            onClick={onReset}
            sx={{ textTransform: 'none' }}
          >
            Start Over
          </Button>
        )}
      </Box>

      {/* Step indicator */}
      <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
        {(['upload', 'mapping', 'processing', 'review', 'done'] as ImportStep[]).map((s, i) => (
          <Box
            key={s}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: i <= ['upload', 'mapping', 'processing', 'review', 'done'].indexOf(step) ? '#10B981' : '#e5e7eb',
              transition: 'background-color 0.3s',
            }}
          />
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* STEP: Upload */}
      {step === 'upload' && (
        <Box
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed #d1d5db',
            borderRadius: '16px',
            p: 8,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s, background-color 0.2s',
            '&:hover': { borderColor: '#10B981', backgroundColor: '#f0fdf4' },
          }}
        >
          <UploadFileIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Drop your bank CSV here
          </Typography>
          <Typography variant="body2" sx={{ color: '#6f7175' }}>
            or click to browse. Supports CSV files from any bank.
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFileUpload(file);
            }}
          />
        </Box>
      )}

      {/* STEP: Column Mapping */}
      {step === 'mapping' && (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Map Columns
          </Typography>
          <Typography variant="body2" sx={{ color: '#6f7175', mb: 3 }}>
            We detected {parsedRows.length} rows in <strong>{fileName}</strong>. Map the columns below.
          </Typography>

          <Autocomplete
            freeSolo
            options={bankAccounts.map((ba) => `${ba.name}${ba.institutionName ? ` (${ba.institutionName})` : ''}${ba.accountNumberLast4 ? ` ••${ba.accountNumberLast4}` : ''}`)}
            value={sourceAccount}
            onInputChange={(_e, value) => onSourceAccountChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Source Account"
                size="small"
                fullWidth
                placeholder="e.g. Chase Business Visa, Mercury Checking, Amex Platinum"
                helperText="Select a connected account or type a new one"
              />
            )}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Date Column"
              select
              size="small"
              sx={{ flex: 1 }}
              value={columnMapping.date}
              onChange={(e) => onColumnMappingChange({ ...columnMapping, date: e.target.value })}
            >
              {headers.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description Column"
              select
              size="small"
              sx={{ flex: 1 }}
              value={columnMapping.description}
              onChange={(e) => onColumnMappingChange({ ...columnMapping, description: e.target.value })}
            >
              {headers.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount Column"
              select
              size="small"
              sx={{ flex: 1 }}
              value={columnMapping.amount}
              onChange={(e) => onColumnMappingChange({ ...columnMapping, amount: e.target.value, debit: '', credit: '' })}
              helperText={columnMapping.debit ? 'Using Debit/Credit instead' : ''}
              disabled={!!(columnMapping.debit && columnMapping.credit)}
            >
              <MenuItem value="">— None —</MenuItem>
              {headers.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <TextField
              label="Debit Column (optional)"
              select
              size="small"
              sx={{ flex: 1 }}
              value={columnMapping.debit}
              onChange={(e) => onColumnMappingChange({ ...columnMapping, debit: e.target.value, amount: '' })}
              helperText="Use instead of Amount"
            >
              <MenuItem value="">— None —</MenuItem>
              {headers.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Credit Column (optional)"
              select
              size="small"
              sx={{ flex: 1 }}
              value={columnMapping.credit}
              onChange={(e) => onColumnMappingChange({ ...columnMapping, credit: e.target.value, amount: '' })}
              helperText="Use instead of Amount"
            >
              <MenuItem value="">— None —</MenuItem>
              {headers.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Card Member (optional)"
              select
              size="small"
              sx={{ flex: 1 }}
              value={columnMapping.cardMember}
              onChange={(e) => onColumnMappingChange({ ...columnMapping, cardMember: e.target.value })}
            >
              <MenuItem value="">— None —</MenuItem>
              {headers.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Preview */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Preview (first 5 rows)
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: '8px', boxShadow: 'none', border: '1px solid #ebebeb', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  {headers.map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedRows.slice(0, 5).map((row) => {
                  const rowKey = row.join('|');
                  return (
                    <TableRow key={rowKey}>
                      {headers.map((header) => (
                        <TableCell key={header} sx={{ fontSize: 12 }}>{row[headers.indexOf(header)]}</TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <FormControlLabel
            control={
              <Checkbox
                checked={invertSign}
                onChange={(e) => onInvertSignChange(e.target.checked)}
                size="small"
              />
            }
            label="Invert sign convention (positive = income, negative = expense)"
            sx={{ mb: 2, '& .MuiFormControlLabel-label': { fontSize: 13, color: '#6f7175' } }}
          />

          <Button
            variant="contained"
            onClick={onStartProcessing}
            disabled={!columnMapping.date || !columnMapping.description || (!columnMapping.amount && (!columnMapping.debit || !columnMapping.credit))}
            sx={{
              backgroundColor: '#10B981',
              '&:hover': { backgroundColor: '#059669' },
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            Process & Categorize
          </Button>
        </Box>
      )}

      {/* STEP: Processing */}
      {step === 'processing' && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <AutoFixHighIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Categorizing transactions...
          </Typography>
          <Typography variant="body2" sx={{ color: '#6f7175', mb: 3 }}>
            Applying rules and AI categorization to {parsedRows.length} transactions.
          </Typography>
          <LinearProgress sx={{ maxWidth: 400, mx: 'auto', '& .MuiLinearProgress-bar': { backgroundColor: '#10B981' } }} />
        </Box>
      )}

      {/* STEP: Review — Summary-first UI */}
      {step === 'review' && (
        <Box>
          {/* Auto-mapped banner */}
          {autoMapped && (
            <Alert
              severity="info"
              sx={{ mb: 3 }}
              action={
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={onGoToMapping}
                  sx={{ textTransform: 'none' }}
                >
                  Edit mapping
                </Button>
              }
            >
              Columns were auto-detected from your CSV headers.
            </Alert>
          )}

          {/* A) Import Summary Card */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: 'none',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Import Summary
                </Typography>
                <Typography variant="body2" sx={{ color: '#6f7175', mt: 0.5 }}>
                  {nonDuplicateRows.length} transaction{nonDuplicateRows.length !== 1 ? 's' : ''} from <strong>{fileName}</strong>
                  {totalExpense > 0 && <> — {formatCurrency(totalExpense)} in expenses</>}
                  {totalIncome > 0 && <>, {formatCurrency(totalIncome)} in income</>}
                </Typography>
              </Box>
            </Box>

            {/* Breakdown chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {ruleCount > 0 && (
                <Chip
                  icon={<RuleIcon />}
                  label={`${ruleCount} rule-matched`}
                  size="small"
                  sx={{ backgroundColor: '#10B98120', color: '#10B981' }}
                />
              )}
              {aiCount > 0 && (
                <Chip
                  icon={<AutoFixHighIcon />}
                  label={`${aiCount} AI-matched`}
                  size="small"
                  sx={{ backgroundColor: '#3B82F620', color: '#3B82F6' }}
                />
              )}
              {unmatchedCount > 0 && (
                <Chip
                  icon={<HelpOutlineIcon />}
                  label={`${unmatchedCount} unmatched`}
                  size="small"
                  sx={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}
                />
              )}
              {subscriptionCount > 0 && (
                <Chip
                  icon={<RepeatIcon />}
                  label={`${subscriptionCount} linked to subscription${subscriptionCount !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ backgroundColor: '#8B5CF620', color: '#8B5CF6' }}
                />
              )}
              {duplicateCount > 0 && (
                <Chip
                  icon={<ContentCopyIcon />}
                  label={`${duplicateCount} duplicate${duplicateCount !== 1 ? 's' : ''} skipped`}
                  size="small"
                  sx={{ backgroundColor: '#EF444420', color: '#EF4444' }}
                />
              )}
            </Box>

            {/* Warning if items need attention */}
            {needsAttentionRows.length > 0 && (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{ mb: 2, borderRadius: '8px' }}
                action={
                  <Button
                    size="small"
                    onClick={onAcceptAll}
                    sx={{ textTransform: 'none' }}
                  >
                    Accept All
                  </Button>
                }
              >
                {needsAttentionRows.length} item{needsAttentionRows.length !== 1 ? 's' : ''} need{needsAttentionRows.length === 1 ? 's' : ''} your attention
              </Alert>
            )}

            {/* Confirm button */}
            <Button
              variant="contained"
              onClick={onConfirmImport}
              disabled={acceptedCount === 0 || isSaving}
              fullWidth
              size="large"
              sx={{
                backgroundColor: '#10B981',
                '&:hover': { backgroundColor: '#059669' },
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.5,
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {isSaving ? 'Importing...' : `Confirm Import (${acceptedCount} transaction${acceptedCount !== 1 ? 's' : ''})`}
            </Button>
          </Paper>

          {/* B) Needs Attention section */}
          {needsAttentionRows.length > 0 && (
            <Paper
              sx={{
                mb: 3,
                borderRadius: '12px',
                border: '1px solid #F59E0B40',
                boxShadow: 'none',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2, backgroundColor: '#FFFBEB', borderBottom: '1px solid #F59E0B40' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Needs Attention ({needsAttentionRows.length})
                </Typography>
                <Typography variant="caption" sx={{ color: '#6f7175' }}>
                  Assign a vendor/category or skip these transactions
                </Typography>
              </Box>
              <Box>
                {needsAttentionRows.map((row) => (
                  <ReviewRowInline
                    key={row.tempId}
                    row={row}
                    vendors={vendors}
                    categories={categories}
                    onUpdateRow={onUpdateRow}
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* C) Auto-Accepted accordion */}
          {autoAcceptedRows.length > 0 && (
            <Accordion
              expanded={showAutoAccepted}
              onChange={(_, expanded) => setShowAutoAccepted(expanded)}
              sx={{
                borderRadius: '12px !important',
                border: '1px solid #e5e7eb',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: '#f9fafb' }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Auto-Accepted ({autoAcceptedRows.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Match</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Confidence</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {autoAcceptedRows.map((row) => (
                        <TableRow
                          key={row.tempId}
                          sx={{
                            backgroundColor: '#f0fdf4',
                            '&:hover': { backgroundColor: '#dcfce7' },
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            // Allow inline editing by marking as not accepted to move to attention
                            onUpdateRow(row.tempId, { accepted: false });
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: 12, color: '#6f7175' }}>
                              {row.date}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={row.originalDescription !== row.description ? `Original: ${row.originalDescription}` : ''} arrow>
                              <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row.description}
                              </Typography>
                            </Tooltip>
                            {row.subscriptionName && (
                              <Chip
                                icon={<RepeatIcon />}
                                label={row.subscriptionName}
                                size="small"
                                sx={{ mt: 0.5, fontSize: 9, height: 18, backgroundColor: '#8B5CF620', color: '#8B5CF6' }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontSize: 12, fontWeight: 600, color: row.type === 'income' ? '#10B981' : '#202123' }}
                            >
                              {row.type === 'income' ? '+' : '-'}{formatCurrency(row.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: 12 }}>
                              {row.vendorId === '__new__'
                                ? row.newVendorName
                                : vendors.find((v) => v.id === row.vendorId)?.displayName
                                  || vendors.find((v) => v.id === row.vendorId)?.name
                                  || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: 12 }}>
                              {categories.find((c) => c.id === row.categoryId)?.name || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {row.matchSource === 'rule' && (
                              <Chip label="Rule" size="small" sx={{ fontSize: 10, height: 20, backgroundColor: '#10B98120', color: '#10B981' }} />
                            )}
                            {row.matchSource === 'ai' && (
                              <Chip label="AI" size="small" sx={{ fontSize: 10, height: 20, backgroundColor: '#3B82F620', color: '#3B82F6' }} />
                            )}
                            {row.matchSource === 'manual' && (
                              <Chip label="Manual" size="small" sx={{ fontSize: 10, height: 20, backgroundColor: '#6B728020', color: '#6B7280' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.confidence !== null && row.confidence > 0 ? (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: '10px',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: confidenceColor(row.confidence),
                                  backgroundColor: `${confidenceColor(row.confidence)}15`,
                                }}
                              >
                                {Math.round(row.confidence * 100)}%
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: 11, color: '#9ca3af' }}>—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {/* STEP: Done */}
      {step === 'done' && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Import Complete
          </Typography>
          <Typography variant="body1" sx={{ color: '#6f7175', mb: 2 }}>
            {acceptedCount} transaction{acceptedCount !== 1 ? 's' : ''} imported from {fileName}.
          </Typography>
          {isLearning && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
              <PsychologyIcon sx={{ fontSize: 20, color: '#8B5CF6' }} />
              <Typography variant="body2" sx={{ color: '#8B5CF6' }}>
                Learning from your import to improve future matches...
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={onReset}
              sx={{ textTransform: 'none', borderRadius: '8px' }}
            >
              Import Another File
            </Button>
            <Button
              variant="contained"
              href="/financex/transactions"
              sx={{
                backgroundColor: '#10B981',
                '&:hover': { backgroundColor: '#059669' },
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              View Transactions
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default FinanceXImportComponent;
